import { motion } from "framer-motion";
import { ArrowRight, Play } from "lucide-react";
import { Button } from "./ui/button";

const WHATSAPP_NUMBER = "528125922909";

// Simplified animations for better mobile performance
const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.5 } }
};

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

export default function HeroSection() {
  const scrollToServices = () => {
    const element = document.querySelector("#services");
    if (element) element.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToContact = () => {
    const element = document.querySelector("#contact");
    if (element) element.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section 
      id="hero" 
      className="relative min-h-screen flex items-center justify-center overflow-hidden hero-gradient"
      data-testid="hero-section"
    >
      {/* Background Elements - simplified */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-[#D4AF37]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#FF6B6B]/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 py-32 lg:py-0 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Text Content */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeIn}
            className="text-left"
          >
            {/* Overline */}
            <p className="overline mb-6">
              Estudio Creativo & Digital
            </p>

            {/* Main Title */}
            <h1 className="font-playfair text-4xl sm:text-5xl lg:text-7xl font-bold text-white leading-tight mb-6">
              Elevamos tu{" "}
              <span className="text-gradient-gold">marca</span>{" "}
              al siguiente nivel
            </h1>

            {/* Subtitle */}
            <p className="text-[#A3A3A3] text-base lg:text-lg leading-relaxed mb-10 max-w-xl">
              Transformamos negocios y marcas con contenido estratégico, 
              diseño web impactante y marketing digital que genera resultados reales.
            </p>

            {/* CTA Buttons */}
            <motion.div
              variants={fadeInUp}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Button
                onClick={scrollToContact}
                className="bg-[#FF6B6B] hover:bg-[#ff5252] text-white font-manrope font-semibold px-8 py-6 rounded-full flex items-center justify-center gap-2 transition-colors duration-200 active:scale-95"
                data-testid="hero-cta-primary"
              >
                Solicitar Cotización
                <ArrowRight size={18} />
              </Button>
              <Button
                onClick={scrollToServices}
                variant="outline"
                className="border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 font-manrope font-semibold px-8 py-6 rounded-full flex items-center justify-center gap-2 transition-colors duration-200 active:scale-95"
                data-testid="hero-cta-secondary"
              >
                Ver Servicios
                <Play size={16} fill="currentColor" />
              </Button>
            </motion.div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-8 mt-16 pt-8 border-t border-white/10">
              {[
                { number: "50+", label: "Proyectos" },
                { number: "3", label: "Años de Exp." },
                { number: "100%", label: "Compromiso" },
              ].map((stat, index) => (
                <div key={index}>
                  <p className="font-playfair text-2xl lg:text-3xl font-bold text-[#D4AF37]">
                    {stat.number}
                  </p>
                  <p className="text-[#A3A3A3] text-sm mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Visual Element - Desktop only */}
          <div className="relative hidden lg:block">
            <div className="relative">
              {/* Main Image */}
              <div className="relative z-10 rounded-lg overflow-hidden border border-white/10">
                <img
                  src="https://images.unsplash.com/photo-1589183440456-34d255090e7b?crop=entropy&cs=srgb&fm=jpg&w=800&q=80"
                  alt="Creative workspace"
                  className="w-full h-[500px] object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
              </div>

              {/* Floating Elements */}
              <div className="absolute -top-8 -right-8 bg-[#121212] border border-white/10 rounded-lg p-4 z-20">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#D4AF37]/20 rounded-full flex items-center justify-center">
                    <span className="text-[#D4AF37] text-lg">✦</span>
                  </div>
                  <div>
                    <p className="text-white text-sm font-semibold">Diseño Premium</p>
                    <p className="text-[#A3A3A3] text-xs">Calidad garantizada</p>
                  </div>
                </div>
              </div>

              <div className="absolute -bottom-6 -left-6 bg-[#121212] border border-white/10 rounded-lg p-4 z-20">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#FF6B6B]/20 rounded-full flex items-center justify-center">
                    <span className="text-[#FF6B6B] text-lg">◆</span>
                  </div>
                  <div>
                    <p className="text-white text-sm font-semibold">Resultados Reales</p>
                    <p className="text-[#A3A3A3] text-xs">Estrategia + Creatividad</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator - Desktop only */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 hidden md:block">
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
          <div className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full animate-bounce" />
        </div>
      </div>
    </section>
  );
}
